import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getFaqKey } from '../utils/keys';

const JapanVisaPage = () => {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: t('jpFaq1Q'),
      answer: t('jpFaq1A')
    },
    {
      question: t('jpFaq2Q'),
      answer: t('jpFaq2A')
    },
    {
      question: t('jpFaq3Q'),
      answer: t('jpFaq3A')
    },
    {
      question: t('jpFaq4Q'),
      answer: t('jpFaq4A')
    },
    {
      question: t('jpFaq5Q'),
      answer: t('jpFaq5A')
    },
    {
      question: t('jpFaq6Q'),
      answer: t('jpFaq6A')
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section 
        className="relative h-[500px] bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1542051841857-5f90071e7989?w=1920&q=80)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl">
            <div className="inline-block bg-yellow-400 text-black px-4 py-2 rounded font-bold mb-4">
              {t('jpHeroBadge')}
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('jpHeroTitle')}
            </h1>
            <p className="text-xl text-white/90 mb-8">
              {t('jpHeroDesc')}
            </p>
            <a
              href="https://wa.me/971585862103?text=Hello%2C%20I%20would%20like%20to%20apply%20for%20a%20Japan%20Visa"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-logo-blue to-logo-cyan text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all"
            >
              {t('jpApplyBtn')}
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        <div className="prose prose-lg max-w-none mb-16">
          <p className="text-gray-700 text-lg leading-relaxed">
            {t('jpIntroP1')}
          </p>
          <p className="text-gray-700 text-lg leading-relaxed mt-4">
            {t('jpIntroP2')}
          </p>
        </div>

        <div className="mb-16 bg-blue-50 p-8 rounded-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('jpFlightsTitle')}</h2>
          <p className="text-gray-700 mb-4">
            {t('jpFlightsDesc')}
          </p>
          <ul className="space-y-2 text-gray-700">
            <li>• <strong>{t('jpFlight1')}</strong></li>
            <li>• <strong>{t('jpFlight2')}</strong></li>
            <li>• <strong>{t('jpFlight3')}</strong></li>
          </ul>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('jpVisaTypesTitle')}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('jpVisaType1Title')}</h3>
              <p className="text-gray-700">{t('jpVisaType1Desc')}</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('jpVisaType2Title')}</h3>
              <p className="text-gray-700">{t('jpVisaType2Desc')}</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('jpVisaType3Title')}</h3>
              <p className="text-gray-700">{t('jpVisaType3Desc')}</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('jpVisaType4Title')}</h3>
              <p className="text-gray-700">{t('jpVisaType4Desc')}</p>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('jpProcessTitle')}</h2>
          
          <div className="space-y-8">
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">1</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('jpStep1Title')}</h3>
                <p className="text-gray-600">{t('jpStep1Desc')}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">2</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('jpStep2Title')}</h3>
                <p className="text-gray-600">{t('jpStep2Desc')}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">3</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('jpStep3Title')}</h3>
                <p className="text-gray-600">{t('jpStep3Desc')}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">4</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('jpStep4Title')}</h3>
                <p className="text-gray-600">{t('jpStep4Desc')}</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">5</div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('jpStep5Title')}</h3>
                <p className="text-gray-600">{t('jpStep5Desc')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-16 bg-gradient-to-r from-logo-blue to-logo-cyan p-8 rounded-lg text-white">
          <h2 className="text-3xl font-bold mb-4">{t('jpWhyChooseTitle')}</h2>
          <ul className="space-y-3 text-lg">
            <li>• <strong>{t('jpWhyChoose1')}</strong></li>
            <li>• <strong>{t('jpWhyChoose2')}</strong></li>
            <li>• <strong>{t('jpWhyChoose3')}</strong></li>
            <li>• <strong>{t('jpWhyChoose4')}</strong></li>
          </ul>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('jpDocsTitle')}</h3>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('jpDoc1')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('jpDoc2')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('jpDoc3')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('jpDoc4')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('jpDoc5')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('jpDoc6')}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>{t('jpDoc7')}</span>
            </li>
          </ul>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">{t('jpFaqTitle')}</h3>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={getFaqKey(faq.question, index)} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-4 bg-gray-50">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>

      <Footer />
    </div>
  );
};

export default JapanVisaPage;
